﻿import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getFirebaseWebFirestore } from "./firebaseWeb";

const USER_ROLES_COLLECTION = "webUserRoles";
const USER_ROLE_AUDIT_COLLECTION = "webUserRoleAuditLogs";
const BOOTSTRAP_SUPER_ADMIN_EMAILS = ["riderstear@gmail.com"];

export const ADMIN_ROLE_OPTIONS = [
  { key: "SuperAdmin", label: "Super Admin" },
  { key: "Admin", label: "Admin" },
  { key: "Moderator", label: "Moderatör" },
  { key: "VerifiedHost", label: "Doğrulanmış Host" },
  { key: "Host", label: "Host" },
  { key: "TestUser", label: "Test User" },
  { key: "User", label: "Kullanıcı" },
];

export const ASSIGNABLE_ADMIN_ROLE_OPTIONS = ADMIN_ROLE_OPTIONS.filter((role) => role.key !== "SuperAdmin");

export const AI_AGENT_FELIX_ROLE = {
  uid: "system-agent-felix",
  email: "felix@system.denkup.internal",
  displayName: "AI Agent Felix",
  role: "Moderator",
  active: true,
  permissions: {
    canAccessAdmin: false,
    canModerateContent: true,
    canUseExperimentalEvents: true,
  },
};

const BASE_USER_ROLE_PERMISSIONS = {
  canManageLevelBoost: false,
  canModerateContent: false,
  canCreatePaidEvents: false,
  canHostLargeEvents: false,
  canUsePrivateEvents: false,
  canUseExperimentalEvents: false,
};

function normalizeEmail(email) {
  return String(email || "").trim().toLocaleLowerCase("tr-TR");
}

function roleDocId({ uid, email }) {
  if (uid) return `uid_${String(uid).replace(/\//g, "_")}`;
  return `email_${normalizeEmail(email).replace(/\//g, "_")}`;
}

function isProtectedSuperAdminEmail(email) {
  return BOOTSTRAP_SUPER_ADMIN_EMAILS.includes(normalizeEmail(email));
}

function buildProfile(data = {}) {
  const role = data.role || "User";
  const active = data.active !== false;
  const explicitPermissions = data.permissions || {};
  const permissions = {
    canAccessAdmin: ["SuperAdmin", "Admin", "Moderator"].includes(role) && active,
    canManageRoles: role === "SuperAdmin" && active,
    canManageLevelBoost: (["SuperAdmin", "Admin"].includes(role) || explicitPermissions.canManageLevelBoost === true) && active,
    canModerateContent: (["SuperAdmin", "Admin", "Moderator"].includes(role) || explicitPermissions.canModerateContent === true) && active,
    canCreateEvents: explicitPermissions.canCreateEvents !== false && active,
    canCreatePaidEvents: (["SuperAdmin", "Admin", "VerifiedHost"].includes(role) || explicitPermissions.canCreatePaidEvents === true) && active,
    canHostLargeEvents: explicitPermissions.canHostLargeEvents === true && active,
    canUsePrivateEvents: explicitPermissions.canUsePrivateEvents === true && active,
    canUseExperimentalEvents: (["SuperAdmin", "Admin"].includes(role) || explicitPermissions.canUseExperimentalEvents === true) && active,
  };

  return {
    uid: data.uid || null,
    email: normalizeEmail(data.email),
    displayName: data.displayName || "",
    role,
    active,
    permissions,
    source: data.source || "firestore",
    roleMissing: Boolean(data.roleMissing),
    updatedAt: data.updatedAt || null,
    roleUpdatedAt: data.roleUpdatedAt || data.updatedAt || null,
    roleUpdatedBy: data.roleUpdatedBy || data.updatedBy || null,
  };
}

export function isBootstrapSuperAdmin(user) {
  const email = normalizeEmail(user?.email);
  return isProtectedSuperAdminEmail(email);
}

export function getBootstrapAdminProfile(user) {
  if (!isBootstrapSuperAdmin(user)) return null;
  return buildProfile({
    uid: user?.uid || null,
    email: user?.email,
    displayName: user?.displayName || "Rider Stear",
    role: "SuperAdmin",
    active: true,
    source: "bootstrap",
  });
}

export async function getAdminAccessProfile(user) {
  if (!user?.uid && !user?.email) return buildProfile({ role: "User", active: false });

  const bootstrapProfile = getBootstrapAdminProfile(user);
  let db;
  try {
    db = getFirebaseWebFirestore();
  } catch (error) {
    if (bootstrapProfile) {
      return {
        ...bootstrapProfile,
        source: "bootstrap-firestore-unavailable",
        roleReadError: error?.message || "Firestore rol profili okunamadı.",
      };
    }
    throw error;
  }

  try {
    const uidRef = doc(db, USER_ROLES_COLLECTION, roleDocId({ uid: user.uid }));
    const uidSnapshot = await getDoc(uidRef);
    if (uidSnapshot.exists()) {
      const firestoreProfile = buildProfile({ ...uidSnapshot.data(), uid: user.uid, email: uidSnapshot.data().email || user.email });
      if (!bootstrapProfile || (firestoreProfile.role === "SuperAdmin" && firestoreProfile.active)) return firestoreProfile;
    }

    const emailRef = doc(db, USER_ROLES_COLLECTION, roleDocId({ email: user.email }));
    const emailSnapshot = await getDoc(emailRef);
    if (emailSnapshot.exists()) {
      const firestoreProfile = buildProfile({ ...emailSnapshot.data(), uid: user.uid, email: user.email });
      if (!bootstrapProfile || (firestoreProfile.role === "SuperAdmin" && firestoreProfile.active)) return firestoreProfile;
    }
  } catch (error) {
    if (bootstrapProfile) {
      return {
        ...bootstrapProfile,
        source: "bootstrap-role-read-error",
        roleReadError: error?.message || "Firestore rol profili okunamadı.",
      };
    }
    throw error;
  }

  if (bootstrapProfile) return bootstrapProfile;

  return buildProfile({ uid: user.uid, email: user.email, role: "User", active: false, source: "role-missing", roleMissing: true });
}

export async function ensureBaseUserRoleProfile(user) {
  if (!user?.uid) return null;
  if (isBootstrapSuperAdmin(user)) return getBootstrapAdminProfile(user);

  const db = getFirebaseWebFirestore();
  const ref = doc(db, USER_ROLES_COLLECTION, roleDocId({ uid: user.uid }));
  const snapshot = await getDoc(ref);
  if (snapshot.exists()) return buildProfile({ ...snapshot.data(), uid: user.uid, email: snapshot.data().email || user.email });

  const payload = {
    uid: user.uid,
    email: normalizeEmail(user.email),
    displayName: user.displayName || "",
    role: "User",
    active: true,
    permissions: BASE_USER_ROLE_PERMISSIONS,
    source: "auth-initial-user-role",
    updatedAt: serverTimestamp(),
    roleUpdatedAt: serverTimestamp(),
    roleUpdatedBy: {
      uid: user.uid,
      email: normalizeEmail(user.email),
    },
  };
  await setDoc(ref, payload, { merge: true });
  return buildProfile(payload);
}

export async function listManagedUserRoles({ max = 80 } = {}) {
  try {
    const db = getFirebaseWebFirestore();
    const constraints = [orderBy("updatedAt", "desc")];
    const safeMax = Number(max);
    if (Number.isFinite(safeMax) && safeMax > 0) {
      constraints.push(limit(Math.max(1, Math.min(safeMax, 1000))));
    }
    const snapshot = await getDocs(query(collection(db, USER_ROLES_COLLECTION), ...constraints));
    return snapshot.docs.map((entry) => ({ id: entry.id, ...buildProfile(entry.data()) }));
  } catch (error) {
    const next = new Error(error?.message || "Yönetilen kullanıcı rolleri Firestore üzerinden okunamadı.");
    next.code = error?.code || "admin-roles/list-failed";
    next.cause = error;
    throw next;
  }
}

export async function upsertManagedUserRole({
  targetUid,
  targetEmail,
  displayName,
  role,
  active,
  actor,
  permissions = {},
}) {
  const actorProfile = await getAdminAccessProfile(actor);
  if (!actorProfile.permissions.canManageRoles) throw new Error("Bu işlem için Super Admin yetkisi gerekir.");

  const safeRole = ADMIN_ROLE_OPTIONS.some((item) => item.key === role) ? role : "User";
  const safeEmail = normalizeEmail(targetEmail);
  if (!targetUid && !safeEmail) throw new Error("Yetki tanımlamak için UID veya e-posta gerekli.");
  if (safeRole === "SuperAdmin") throw new Error("Super Admin tek hesaptır ve manuel atanamaz.");
  if (isProtectedSuperAdminEmail(safeEmail)) throw new Error("Bootstrap Super Admin e-postası panelden değiştirilemez.");

  const db = getFirebaseWebFirestore();

  const permissionPayload = {
    canManageLevelBoost: Boolean(permissions.canManageLevelBoost),
    canModerateContent: Boolean(permissions.canModerateContent),
    canCreatePaidEvents: Boolean(permissions.canCreatePaidEvents),
    canHostLargeEvents: Boolean(permissions.canHostLargeEvents),
    canUsePrivateEvents: Boolean(permissions.canUsePrivateEvents),
    canUseExperimentalEvents: Boolean(permissions.canUseExperimentalEvents),
  };
  const roleUpdatedBy = {
    uid: actor?.uid || null,
    email: normalizeEmail(actor?.email),
  };
  const payload = {
    uid: targetUid || null,
    email: safeEmail,
    displayName: String(displayName || "").trim(),
    role: safeRole,
    active: Boolean(active),
    permissions: permissionPayload,
    updatedAt: serverTimestamp(),
    updatedBy: roleUpdatedBy,
    roleUpdatedAt: serverTimestamp(),
    roleUpdatedBy,
  };

  const ref = doc(db, USER_ROLES_COLLECTION, roleDocId({ uid: targetUid, email: safeEmail }));
  await setDoc(ref, payload, { merge: true });
  await addDoc(collection(db, USER_ROLE_AUDIT_COLLECTION), {
    targetRoleId: ref.id,
    targetUid: targetUid || null,
    targetEmail: safeEmail,
    displayName: payload.displayName,
    role: safeRole,
    active: Boolean(active),
    permissions: permissionPayload,
    roleUpdatedAt: serverTimestamp(),
    roleUpdatedBy,
    source: "denkup-com-admin-panel",
  });
  return { id: ref.id, ...payload };
}
